import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/api/client';
import type { ChatResponse, Conversation, ConversationDetail } from '../types/chat';

const BASE = '/ai';

export const aiKeys = {
  all: ['ai'] as const,
  conversations: () => [...aiKeys.all, 'conversations'] as const,
  conversation: (id: number | null) => [...aiKeys.all, 'conversation', id] as const,
};

export function useConversations() {
  return useQuery({
    queryKey: aiKeys.conversations(),
    queryFn: async () => {
      const { data } = await api.get<Conversation[]>(`${BASE}/conversations`);
      return data;
    },
  });
}

export function useConversation(id: number | null) {
  return useQuery({
    queryKey: aiKeys.conversation(id),
    queryFn: async () => {
      const { data } = await api.get<ConversationDetail>(`${BASE}/conversations/${id}`);
      return data;
    },
    enabled: id !== null && id > 0,
  });
}

// Las peticiones al asistente pueden tardar mucho (Ollama carga modelos a RAM,
// procesa imágenes con VLM, etc.). Subimos el timeout HTTP a 5 minutos solo
// para estas llamadas (el cliente general sigue en 30s).
const AI_TIMEOUT = 5 * 60 * 1000;

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      message: string;
      conversation_id: number | null;
      files?: File[];
    }) => {
      // Si hay archivos, mandamos multipart. Si no, JSON normal.
      if (payload.files && payload.files.length > 0) {
        const fd = new FormData();
        fd.append('message', payload.message);
        if (payload.conversation_id !== null) {
          fd.append('conversation_id', String(payload.conversation_id));
        }
        payload.files.forEach((f) => fd.append('files[]', f));
        const { data } = await api.post<ChatResponse>(`${BASE}/chat`, fd, {
          timeout: AI_TIMEOUT,
        });
        return data;
      }
      const { data } = await api.post<ChatResponse>(
        `${BASE}/chat`,
        {
          message: payload.message,
          conversation_id: payload.conversation_id,
        },
        { timeout: AI_TIMEOUT },
      );
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: aiKeys.conversations() });
      qc.invalidateQueries({ queryKey: aiKeys.conversation(data.conversation_id) });
    },
  });
}

/** URL absoluta para descargar/ver un adjunto del asistente. */
export function attachmentUrl(id: number): string {
  // Usa el mismo baseURL de api (cookie auth funciona porque withCredentials).
  const base = api.defaults.baseURL ?? '';
  return `${base}${BASE}/attachments/${id}`;
}

/* ─────────────────── Modo escritura: confirmar / cancelar ─────────────────── */

export function useConfirmAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (actionId: number) => {
      const { data } = await api.post<{ ok: boolean; affected_rows: number; executed_at: string }>(
        `${BASE}/actions/${actionId}/confirm`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
    },
  });
}

export function useCancelAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (actionId: number) => {
      const { data } = await api.post<{ ok: boolean }>(`${BASE}/actions/${actionId}/cancel`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.all });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`${BASE}/conversations/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: aiKeys.conversations() });
    },
  });
}
